import React, { useEffect, useRef, useState } from "react"
import Transition from "../utils/Transition.js"
import { graphql, useStaticQuery } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import { analyticsAction } from "/src/utils/analytics"

const initialItems = [
  {
    img: "film/projects/the-gentlemen.jpg",
    url: "https://www.imdb.com/title/tt13210838/",
    alt: "The Gentlemen Project",
    name: "The Gentlemen",
    company: "Netflix",
  },

  {
    img: "film/projects/black-angel.jpg",
    url: "https://www.imdb.com/title/tt4733704/",
    alt: "Black Angel Project",
    name: "Black Angel",
    company: "Indie",
  },

  {
    img: "film/projects/nobody-2.jpg",
    url: "https://www.imdb.com/title/tt28996126/",
    alt: "Nobody 2 Project",
    name: "Nobody 2",
    company: "Universal Pictures",
  },

  {
    img: "film/projects/masters-of-the-air.jpg",
    url: "https://www.imdb.com/title/tt2640044/",
    alt: "Masters of the Air Project",
    name: "Masters of the Air",
    company: "Apple TV+",
  },

  {
    img: "film/projects/gangs-of-london.jpg",
    url: "https://www.imdb.com/title/tt7661390/",
    alt: "Gangs of London Project",
    name: "Gangs of London",
    company: "amc+",
  },

  {
    img: "film/projects/1978.jpg",
    url: "https://www.imdb.com/name/nm0464366/",
    alt: "1978 Project",
    name: "1978",
    company: "Indie",
  },

  {
    img: "film/projects/salem-2.jpg",
    url: "https://www.dstv.com/maishamagicplus/en-ke/show/salem/season/2",
    alt: "Salem Project",
    name: "Salem",
    company: "DStv",
  },

  {
    img: "film/projects/cannibal-mind.jpg",
    url: "https://www.imdb.com/title/tt11642948/",
    alt: "A Cannibal Mind Project",
    name: "A Cannibal Mind",
    company: "Indie",
  },

  {
    img: "film/projects/and-then-you-die.jpg",
    url: "https://www.imdb.com/title/tt33999407/",
    alt: "And Then You Die Project",
    name: "And Then You Die",
    company: "Indie",
  },

  {
    img: "film/projects/submit.png",
    url: "mailto:halloffame@filmustage.com?subject=Submit%20my%20project%20to%20the%20Hall%20of%20Fame&body=Hey!%0AI%E2%80%99d%20like%20to%20submit%20my%20project%20to%20the%20Hall%20of%20Fame!%20%F0%9F%8D%BF%0A%0AProject%20Name%3A%0AURL%3A%0AMy%20Use%20Case%3A",
    alt: "Submit your project made using Filmustage",
    name: "Submit your project",
  },
]

function HallOfFame({ data }) {
  const dataQuery = useStaticQuery(graphql`
    query {
      allFile(
        filter: {
          sourceInstanceName: { eq: "images" }
          extension: { in: ["jpeg", "jpg", "png"] }
        }
      ) {
        edges {
          node {
            relativePath
            childImageSharp {
              gatsbyImageData(
                width: 300
                height: 450
                quality: 80
                layout: CONSTRAINED
              )
            }
          }
        }
      }
    }
  `)
  const [active, setActive] = useState(0)
  const [isClickable, setIsClickable] = useState(true)
  const [autorotate, setAutorotate] = useState(false)
  const [autorotateTiming] = useState(7000)
  //https://github.com/gatsbyjs/gatsby/discussions/36232

  const [items] = useState(!data ? initialItems : [...initialItems, ...data])
  const testimonials = useRef(null)

  const stopAutorotate = () => {
    setAutorotate(null)
  }

  const handleClick = callback => {
    if (!isClickable) return

    setIsClickable(false)
    callback()

    setTimeout(() => {
      setIsClickable(true)
    }, 500)
  }

  const changeProject = direction => {
    const groupCount = Math.ceil(items.length / 4)
    let newIndex = active + direction

    if (newIndex < 0) {
      newIndex = groupCount - 1
    } else if (newIndex >= groupCount) {
      newIndex = 0
    }

    setActive(newIndex)
    stopAutorotate()
  }

  const selectBullet = index => {
    setActive(index)
  }

  const heightFix = () => {
    const initClass = [...testimonials.current.children[active].classList]
    const checkFirstRun = initClass.filter(cl => cl === "appear").length

    if (checkFirstRun !== 1 && testimonials.current.children[active]) {
      testimonials.current.style.height =
        testimonials.current.children[active].offsetHeight + "px"
    }
  }

  // noinspection DuplicatedCode
  useEffect(() => {
    if (!autorotate) return

    const groupCount = Math.ceil(items.length / 4)

    const interval = setInterval(() => {
      setActive(prevActive =>
        prevActive + 1 === groupCount ? 0 : prevActive + 1,
      )
    }, autorotateTiming)

    return () => clearInterval(interval)
  }, [active, autorotate, items.length, autorotateTiming])

  useEffect(() => {
    const handleResize = () => {
      heightFix()
    }

    heightFix()

    window.addEventListener("resize", handleResize)

    return () => {
      window.removeEventListener("resize", handleResize)
    }
  }, [active])

  const renderProjects = projects => {
    return projects.map((item, index) => {
      const image = item.img
        ? dataQuery.allFile.edges.find(
            edge => edge.node.relativePath === item.img,
          )?.node.childImageSharp.gatsbyImageData
        : null

      return (
        <a
          href={item.url}
          onClick={e => {
            if (item.url.indexOf("mailto") !== -1 && window.Intercom) {
              e.preventDefault()
              window.Intercom(
                "showNewMessage",
                "Hey! I’d like to submit my project to the Hall of Fame! 🍿",
              )
            }
            analyticsAction("landing_submit_project")
          }}
          className="flex flex-col flex-grow h-full p-4 bg-gray-800 shadow-2xl hover:shadow-orange-400/30 shadow-indigo-500/30 transform hover:scale-[1.01] transition duration-500 ease-out"
          key={index}
          target="_blank"
          rel="noopener noreferrer"
        >
          {image && (
            <GatsbyImage
              image={image}
              alt={item.alt}
              className="h-full w-full"
              imgStyle={{ objectFit: "cover" }}
            />
          )}
          <div className="text-gray-700 font-medium mt-4 pt-3 border-t border-gray-700">
            {item.url.indexOf("mailto") === -1 ? (
              <cite className="text-gray-200 not-italic">{item.name}</cite>
            ) : (
              <div className="relative pb-6 md:pb-0">
                <span className="btn absolute -top-11 md:-top-12 -left-0 px-3 text-gray-800 bg-fOrange-900 hover:bg-fOrange-800 w-full capitalize">
                  &nbsp;<span className="">{item.name}</span>
                </span>
              </div>
            )}

            {item.company && (
              <>
                <span className="text-gray-600"> / </span>
                <span className="text-fOrange-900">{item.company}</span>
              </>
            )}
          </div>
        </a>
      )
    })
  }

  const groupProjects = items => {
    const groups = []

    for (let i = 0; i < items.length; i += 4) {
      groups.push(items.slice(i, i + 4))
    }

    return groups
  }

  const projectGroups = groupProjects(items)

  return (
    <section id="projects">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="py-12 md:py-20 border-t border-gray-800">
          {/* Section header */}
          <div
            className="max-w-3xl mx-auto text-center pb-8 md:pb-12"
            data-aos-id-projectscar
          >
            <h2
              className="h2 mb-4 "
              data-aos="fade-up"
              data-aos-anchor="[data-aos-id-projectscar]"
            >
              Hall of Fame
            </h2>
            <p
              className="text-xl text-gray-400"
              data-aos="fade-up"
              data-aos-delay="200"
              data-aos-anchor="[data-aos-id-projectscar]"
            >
              Standout projects created with Filmustage.
            </p>
          </div>

          {/* Carousel area*/}
          <div
            className="max-w-3xl md:max-w-6xl mx-auto text-center"
            data-aos="fade-up"
            data-aos-delay="300"
            data-aos-anchor="[data-aos-id-projectscar]"
          >
            {/* Bullets */}
            <div className="flex justify-center flex-wrap mb-8">
              {projectGroups.map((item, index) => (
                <button
                  className="p-1 group"
                  key={index}
                  onClick={() => {
                    handleClick(() => selectBullet(index))
                    stopAutorotate()
                    analyticsAction("landing_change_projects")
                  }}
                  aria-label="Projects"
                >
                  <span
                    className={`block w-3 h-3 rounded-full group-hover:bg-gray-400 transition duration-150 ease-in-out ${
                      active === index ? "bg-gray-200" : "bg-gray-500"
                    }`}
                  />
                </button>
              ))}
            </div>

            <div className="flex justify-between items-center max-w-6xl">
              <div className="flex justify-center items-center">
                <button
                  aria-label="Previous project"
                  className="w-20 h-20 !absolute ml-12 lg:-ml-14 z-[1] text-gray-500 hover:text-gray-400 transition duration-150 ease-in-out bg-gray-800 lg:bg-transparent opacity-70 lg:opacity-100"
                  onClick={() => {
                    handleClick(() => changeProject(-1))
                    analyticsAction("landing_change_projects")
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="0.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="15 18 9 12 15 6"></polyline>
                  </svg>
                </button>
              </div>

              {/* Projects */}
              <div
                className="relative flex items-start justify-between mx-auto text-center"
                ref={testimonials}
              >
                {groupProjects(items).map((group, index) => (
                  <Transition
                    key={index}
                    show={active === index}
                    appear={true}
                    className="grid gap-2 lg:gap-4 grid-cols-2 md:grid-cols-4 w-full"
                    enter="transition ease-in-out duration-500 transform order-first"
                    enterStart="opacity-0 scale-98"
                    enterEnd="opacity-100 scale-100"
                    leave="transition ease-out duration-300 transform absolute"
                    leaveStart="opacity-100 scale-100"
                    leaveEnd="opacity-0 scale-98"
                  >
                    {renderProjects(group)}
                  </Transition>
                ))}
              </div>

              <div className="flex justify-center items-center">
                <button
                  aria-label="Next project"
                  className="w-20 h-20 !absolute mr-14 lg:-mr-14 z-[1] text-gray-500 hover:text-gray-400 transition duration-150 ease-in-out bg-gray-800 lg:bg-transparent opacity-70 lg:opacity-100"
                  onClick={() => {
                    handleClick(() => changeProject(1))
                    analyticsAction("landing_change_projects")
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="0.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="9 18 15 12 9 6"></polyline>
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default HallOfFame
